<template>
  <dx-modal
    :value="true"
    title="Nota Fiscal Eletrônica"
    @input="close"
    size="lg"
    id="integration-fiscal-document-modal"
  >
    <div v-if="loading" class="text-center p-2">
      <div class="loading loading-lg"></div>
    </div>

    <st-tabs v-else>
      <st-tab name="Cadastro da empresa">
        <div class="columns mt-2">
          <div
            class="column col-4 form-group"
            :class="{'has-error': $v.form.identity.$error}"
          >
            <label for="identity" class="form-label">* CNPJ</label>
            <input
              class="form-input"
              id="identity"
              type="text"
              v-model="form.identity"
              placeholder="00.000.000/0000-00"
              v-mask-cnpj
            >
            <st-form-input-hint :validator="$v.form.identity" />
          </div>
          <div
            class="column col-8 form-group"
            :class="{'has-error': $v.form.companyName.$error}"
          >
            <label for="company-name" class="form-label">* Razão social</label>
            <input
              class="form-input"
              id="company-name"
              type="text"
              v-model="form.companyName"
              placeholder="Razão social"
            >
            <st-form-input-hint :validator="$v.form.companyName" />
          </div>
          <div
            class="column col-4 form-group"
            :class="{'has-error': $v.form.email.$error}"
          >
            <label for="email" class="form-label">* Email</label>
            <input
              class="form-input"
              id="email"
              type="text"
              v-model="form.email"
              placeholder="email@email.com"
            >
            <st-form-input-hint :validator="$v.form.email" />
          </div>
          <div
            class="column col-3 form-group"
            :class="{'has-error': $v.form.phone.$error}"
          >
            <label for="phone" class="form-label">Telefone</label>
            <input
              class="form-input"
              id="phone"
              type="text"
              v-model="form.phone"
              placeholder="(00) 0000-0000"
              v-mask-phone.br
            >
            <st-form-input-hint :validator="$v.form.phone" />
          </div>
          <div class="column col-5 form-group">
            <label for="name" class="form-label">Nome fantasia</label>
            <input
              class="form-input"
              id="name"
              type="text"
              v-model="form.name"
              placeholder="Nome fantasia"
            >
          </div>
        </div>

        <div class="columns mt-2">
          <div
            class="column col-4 form-group"
            :class="{'has-error': $v.form.address.postalCode.$error}"
          >
            <label for="address-postal-code" class="form-label">* CEP</label>
            <div class="input-group">
              <input
                class="form-input"
                id="address-postal-code"
                type="text"
                v-model="form.address.postalCode"
                @change="loadAddressByPostalCode"
                placeholder="00.000-000"
                v-mask-cep
              >
              <button
                type="button"
                class="btn input-group-btn btn-icon btn-action btn-gray-outline"
                :class="{ loading: loadingAddress }"
                @click="loadAddressByPostalCode"
                :disabled="loadingAddress"
              ><fa-icon :icon="['fal', 'search']" /></button>
            </div>
            <st-form-input-hint :validator="$v.form.address.postalCode" />
          </div>
          <div class="column col-8"></div>
          <div
            class="column col-6 form-group"
            :class="{'has-error': $v.form.address.street.$error}"
          >
            <label for="address-street" class="form-label">* Endereço</label>
            <input
              class="form-input"
              id="address-street"
              type="text"
              v-model="form.address.street"
              :disabled="!isPostalCodeValid"
              placeholder="Avenida Brasil"
            >
            <st-form-input-hint :validator="$v.form.address.street" />
          </div>
          <div
            class="column col-2 form-group"
            :class="{'has-error': $v.form.address.number.$error}"
          >
            <label for="address-number" class="form-label">* Número</label>
            <input
              class="form-input"
              id="address-number"
              type="text"
              v-model="form.address.number"
              :disabled="!isPostalCodeValid"
              placeholder="0"
            >
            <st-form-input-hint :validator="$v.form.address.number" />
          </div>
          <div class="column col-4 form-group">
            <label for="address-complement" class="form-label">Complemento</label>
            <input
              class="form-input"
              id="address-complement"
              type="text"
              v-model="form.address.complement"
              :disabled="!isPostalCodeValid"
              placeholder="Complemento"
            >
          </div>
          <div class="column col-4 form-group">
            <label for="address-neighborhood" class="form-label">Bairro</label>
            <input
              class="form-input"
              id="address-neighborhood"
              type="text"
              v-model="form.address.neighborhood"
              :disabled="!isPostalCodeValid"
              placeholder="Bairro"
            >
          </div>
          <div
            class="column col-4 form-group"
            :class="{'has-error': $v.form.address.city.$error}"
          >
            <label for="address-city" class="form-label">* Cidade</label>
            <input
              class="form-input"
              id="address-city"
              type="text"
              v-model="form.address.city"
              :disabled="!isPostalCodeValid"
              :readonly="isPostalCodeValid"
              placeholder="Cidade"
            >
            <st-form-input-hint :validator="$v.form.address.city" />
          </div>
          <div
            class="column col-4 form-group"
            :class="{'has-error': $v.form.address.state.$error}"
          >
            <label for="address-state" class="form-label">* Estado</label>
            <select
              id="address-state"
              name="address-state"
              class="form-select"
              :class="{ readonly: isPostalCodeValid }"
              v-model="form.address.state"
              :disabled="true"
            >
              <option value="">[Selecione]</option>
              <option v-for="(text, value) in states"
                      :value="value" :key="value">{{ text }}</option>
            </select>
            <st-form-input-hint :validator="$v.form.address.state" />
          </div>
        </div>
      </st-tab>

      <st-tab name="Configurações">
        <div class="upload-area xml-import mt-2" :class="{ dragging: xmlImport.dragging }">
          <input
            type="file"
            accept=".xml,text/xml,application/xml"
            class="input-file"
            :disabled="xmlImport.loading"
            @dragenter="xmlImport.dragging = true"
            @dragleave="xmlImport.dragging = false"
            @drop="xmlImport.dragging = false"
            @change="xmlUploadChange"
          >
          <div class="upload-inner">
            <fa-icon
              v-if="xmlImport.loading"
              :icon="['fal', 'spinner-third']"
              :spin="true"
            />
            <fa-icon v-else :icon="['fal', 'file-invoice']" />
            <p>
              Já emite nota por fora? Arraste aqui o XML de uma NFS-e emitida
              para preencher os campos automaticamente<br>
              <small>
                (Portal do Emissor Nacional ou prefeitura/ABRASF — nada é enviado
                para o servidor, confira os dados e clique em Salvar)
              </small>
            </p>
          </div>
        </div>

        <div class="divider" />

        <div class="columns mt-2">
          <div
            class="column col-4 form-group"
            :class="{'has-error': $v.form.municipalRegistration.$error}"
          >
            <label
              for="municipal-registration"
              class="form-label"
            >* Inscrição Municipal</label>
            <input
              class="form-input"
              id="municipal-registration"
              type="text"
              v-model="form.municipalRegistration"
              placeholder="Inscrição Municipal"
            >
            <st-form-input-hint :validator="$v.form.municipalRegistration" />
          </div>

          <div class="column col-4 form-group">
            <label for="fiscal-provider" class="form-label">Provedor de emissão</label>
            <select id="fiscal-provider" class="form-select" v-model="form.provider">
              <option
                v-for="item in providers"
                :value="item.value"
                :key="item.value"
              >{{ item.label }}</option>
            </select>
          </div>

          <div
            class="column col-4 form-group"
            :class="{'has-error': $v.form.taxRegime.$error}"
          >
            <label for="tax-regime" class="form-label">* Regime Tributário</label>
            <select
              id="tax-regime"
              class="form-select"
              v-model="form.taxRegime"
              :disabled="companyTaxRegime.loading"
            >
              <option value="">[Selecione]</option>
              <option
                v-for="(text, value) in availableTaxRegimes"
                :value="value"
                :key="value"
              >{{ text }}</option>
            </select>
            <st-form-input-hint :validator="$v.form.taxRegime" />
          </div>

          <div class="column col-12 form-group" v-if="companyTaxRegimeFeedback">
            <div class="company-tax-regime" :class="companyTaxRegimeFeedback.type">
              <fa-icon
                :icon="['fal', companyTaxRegimeFeedback.icon]"
                :spin="companyTaxRegime.loading"
              />
              <span>{{ companyTaxRegimeFeedback.message }}</span>
            </div>
          </div>

          <div class="column col-12 form-group" v-if="isSimplesNacional">
            <label for="tax-calculation-regime" class="form-label">
              Regime de Apuração Tributária pelo Simples Nacional
            </label>
            <select
              id="tax-calculation-regime"
              class="form-select"
              v-model="form.taxCalculationRegimeSN"
            >
              <option value="">[Selecione]</option>
              <option value="1">Tributos federais e municipal pelo Simples Nacional</option>
              <option value="2">Tributos federais pelo SN e ISSQN por fora do SN</option>
            </select>
          </div>

          <div class="column col-4 form-group">
            <label for="cultural-incentive" class="form-label">Incentivador Cultural</label>
            <select id="cultural-incentive" class="form-select" v-model="form.culturalIncentive">
              <option :value="false">Não</option>
              <option :value="true">Sim</option>
            </select>
          </div>

          <div
            class="column col-8 form-group"
            :class="{'has-error': $v.form.specialTaxRegime.$error}"
          >
            <label
              for="special-tax-regime"
              class="form-label"
            >Tipo do regime especial de tributação</label>
            <select id="special-tax-regime" class="form-select" v-model="form.specialTaxRegime">
              <option value="">[Selecione]</option>
              <option
                v-for="item in specialTaxRegimes"
                :value="item.code"
                :key="item.code"
              >{{ item.description }}</option>
            </select>
            <st-form-input-hint :validator="$v.form.specialTaxRegime" />
          </div>

          <div class="column col-12 form-group" v-if="regimeGuide">
            <div
              class="regime-guide"
              :class="regimeGuide.pending.length ? 'regime-guide-pending' : 'regime-guide-ok'"
            >
              <div class="regime-guide-header">
                <fa-icon
                  :icon="['fal', regimeGuide.pending.length ? 'lightbulb' : 'check-circle']"
                />
                <div class="regime-guide-title">
                  <strong>{{ regimeGuide.title }}</strong>
                  <small>{{ regimeGuide.subtitle }}</small>
                </div>
                <button
                  v-if="regimeGuide.pending.length"
                  type="button"
                  class="btn btn-primary btn-sm"
                  @click="applyRegimeGuide"
                >Aplicar padrão</button>
              </div>
              <div class="regime-guide-rows" v-if="regimeGuide.pending.length">
                <div
                  class="regime-guide-row"
                  v-for="row in regimeGuide.rows"
                  :key="row.key"
                >
                  <div class="regime-guide-label">
                    <span>{{ row.label }}</span>
                    <small v-if="row.hint">{{ row.hint }}</small>
                  </div>
                  <div class="regime-guide-expected">{{ row.expected }}</div>
                  <div class="regime-guide-status">
                    <span class="regime-guide-pill ok" v-if="row.ok">
                      <fa-icon :icon="['fal', 'check']" />Ok
                    </span>
                    <span class="regime-guide-pill diff" v-else>Atual: {{ row.current }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="divider text-center" data-content="Serviço padrão"></div>

        <div class="columns">
          <div
            class="column col-12 form-group"
            :class="{'has-error': $v.form.service.nationalTaxationCode.$error}"
          >
            <label for="service-national-code" class="form-label">* Cód. Tributação Nacional</label>
            <template v-if="isNationalProvider">
              <dx-autocomplete
                input-id="service-national-code"
                :value="selectedNationalCode"
                :source="findNationalCode"
                :custom-label="nationalCodeLabel"
                :disabled="!!selectedNationalCode"
                :min-len="2"
                :debounce="150"
                placeholder="Pesquise pelo código ou pelo serviço (ex.: fonoaudiologia)"
                @input="setNationalCode"
              >
                <template v-slot:action>
                  <button
                    class="btn btn-action input-group-btn btn-icon"
                    :class="selectedNationalCode ? 'btn-gray' : 'btn-neutral'"
                    tabindex="-1"
                    @click="setNationalCode(null)"
                  >
                    <fa-icon :icon="['fal', selectedNationalCode ? 'times' : 'search']" />
                  </button>
                </template>
                <template v-slot="{ item }">
                  <div class="nbs-option">
                    <span class="nbs-option-code">{{ formatNationalCode(item.code) }}</span>
                    <span>
                      {{ item.name }}
                      <small v-if="item.group !== item.name" class="option-group">
                        {{ item.subitem }} {{ item.group }}
                      </small>
                    </span>
                  </div>
                </template>
              </dx-autocomplete>
              <p
                v-if="selectedNationalCode && !selectedNationalCode.name"
                class="form-input-hint field-warning"
              >
                Este código não existe na lista nacional de serviços. A nota será rejeitada.
                Pesquise e escolha o código correto.
              </p>
            </template>
            <input
              v-else
              type="text"
              class="form-input"
              id="service-national-code"
              v-model="form.service.nationalTaxationCode"
              placeholder="Ex.: 04.03.01"
            >
            <st-form-input-hint :validator="$v.form.service.nationalTaxationCode" />
          </div>
          <div
            class="column col-12 form-group"
            :class="{'has-error': $v.form.service.nbsCode.$error}"
          >
            <label for="service-nbs-code" class="form-label">
              {{ isNbsRequired ? '* ' : '' }}Código NBS
            </label>
            <dx-autocomplete
              input-id="service-nbs-code"
              :value="selectedNbs"
              :source="findNbs"
              :custom-label="nbsLabel"
              :disabled="!!selectedNbs"
              :min-len="2"
              :debounce="150"
              placeholder="Pesquise pelo código ou pelo serviço (ex.: clínica médica)"
              @input="setNbs"
            >
              <template v-slot:action>
                <button
                  class="btn btn-action input-group-btn btn-icon"
                  :class="selectedNbs ? 'btn-gray' : 'btn-neutral'"
                  tabindex="-1"
                  @click="setNbs(null)"
                >
                  <fa-icon :icon="['fal', selectedNbs ? 'times' : 'search']" />
                </button>
              </template>
              <template v-slot="{ item }">
                <div class="nbs-option">
                  <span class="nbs-option-code">{{ item.code }}</span>
                  <span>{{ item.name }}</span>
                </div>
              </template>
            </dx-autocomplete>
            <st-form-input-hint :validator="$v.form.service.nbsCode" />
            <div
              class="nbs-suggestions"
              :class="{ 'nbs-suggestions-mismatch': form.service.nbsCode }"
              v-if="nbsSuggestions.length && !isNbsSuggested"
            >
              <div class="nbs-suggestions-header">
                <fa-icon
                  :icon="['fal', form.service.nbsCode ? 'exclamation-triangle' : 'lightbulb']"
                />
                <span v-if="form.service.nbsCode">
                  Este NBS não corresponde ao código {{ selectedNationalCode.subitem }}.
                  Escolha um dos previstos na tabela nacional.
                </span>
                <span v-else>
                  NBS previstos para o código {{ selectedNationalCode.subitem }}
                </span>
              </div>
              <div class="nbs-suggestions-list">
                <button
                  v-for="item in visibleNbsSuggestions"
                  :key="item.code"
                  type="button"
                  class="nbs-suggestion"
                  @click="setNbs(item)"
                >
                  <span class="nbs-option-code">{{ item.code }}</span>
                  <span class="nbs-suggestion-name" :title="item.name">{{ item.name }}</span>
                  <span class="nbs-suggestion-action">Usar</span>
                </button>
                <button
                  v-if="hiddenNbsSuggestions > 0"
                  type="button"
                  class="nbs-suggestion nbs-suggestion-more"
                  @click="nbsSuggestionsExpanded = true"
                >
                  Ver mais {{ hiddenNbsSuggestions }}
                  <fa-icon :icon="['fal', 'chevron-down']" />
                </button>
              </div>
            </div>
          </div>
          <div class="column col-6 form-group">
            <label for="service-municipal-code" class="form-label">Código Municipal</label>
            <input
              type="text"
              class="form-input"
              id="service-municipal-code"
              v-model="form.service.municipalCode"
              placeholder="Opcional"
            >
          </div>
          <div class="column col-6 form-group">
            <label for="service-cnae" class="form-label">CNAE</label>
            <input
              type="text"
              class="form-input"
              id="service-cnae"
              v-model="form.service.cnae"
              placeholder="Código CNAE"
            >
          </div>
          <div
            class="column col-12 form-group mb-2"
            :class="{'has-error': $v.form.service.description.$error}"
          >
            <label for="service-description" class="form-label">* Descrição</label>
            <textarea
              type="text"
              class="form-input"
              id="service-description"
              v-model="form.service.description"
              placeholder="Descrição do serviço"
            />
            <st-form-input-hint :validator="$v.form.service.description" />
          </div>
        </div>

        <div class="divider text-center" data-content="Impostos padrão"></div>

        <div class="columns">
          <div
            class="column col-3 form-group"
            :class="{'has-error': $v.form.service.issRate.$error}"
          >
            <label for="iss-rate" class="form-label">* ISS</label>
            <div class="input-group">
              <dx-input-number
                class="form-input"
                id="iss-rate"
                v-model="form.service.issRate"
                maxlength="5"
                :precision="2"
              />
              <span class="input-group-addon">%</span>
            </div>
            <st-form-input-hint :validator="$v.form.service.issRate" />
          </div>
          <div class="column col-3 form-group">
            <label for="ir-rate" class="form-label">IR</label>
            <div class="input-group">
              <dx-input-number
                class="form-input"
                id="ir-rate"
                v-model="form.service.irRate"
                maxlength="5"
                :precision="2"
              />
              <span class="input-group-addon">%</span>
            </div>
          </div>
          <div class="column col-3 form-group">
            <label for="pis-rate" class="form-label">PIS</label>
            <div class="input-group">
              <dx-input-number
                class="form-input"
                id="pis-rate"
                v-model="form.service.pisRate"
                maxlength="5"
                :precision="2"
              />
              <span class="input-group-addon">%</span>
            </div>
          </div>
          <div class="column col-3 form-group">
            <label for="cofins-rate" class="form-label">COFINS</label>
            <div class="input-group">
              <dx-input-number
                class="form-input"
                id="cofins-rate"
                v-model="form.service.cofinsRate"
                maxlength="5"
                :precision="2"
              />
              <span class="input-group-addon">%</span>
            </div>
          </div>
          <div class="column col-3 form-group">
            <label for="csll-rate" class="form-label">CSLL</label>
            <div class="input-group">
              <dx-input-number
                class="form-input"
                id="csll-rate"
                v-model="form.service.csllRate"
                maxlength="5"
                :precision="2"
              />
              <span class="input-group-addon">%</span>
            </div>
          </div>
          <div class="column col-3 form-group">
            <label for="inss-rate" class="form-label">INSS</label>
            <div class="input-group">
              <dx-input-number
                class="form-input"
                id="inss-rate"
                v-model="form.service.inssRate"
                maxlength="5"
                :precision="2"
              />
              <span class="input-group-addon">%</span>
            </div>
          </div>
          <div class="column col-3 form-group" v-if="!isSimplesOrMei">
            <label for="cbs-rate" class="form-label">CBS</label>
            <div class="input-group">
              <dx-input-number
                class="form-input"
                id="cbs-rate"
                v-model="form.service.cbsRate"
                maxlength="5"
                :precision="2"
              />
              <span class="input-group-addon">%</span>
            </div>
          </div>
          <div class="column col-3 form-group" v-if="!isSimplesOrMei">
            <label for="ibs-rate" class="form-label">IBS</label>
            <div class="input-group">
              <dx-input-number
                class="form-input"
                id="ibs-rate"
                v-model="form.service.ibsRate"
                maxlength="5"
                :precision="2"
              />
              <span class="input-group-addon">%</span>
            </div>
          </div>
        </div>

        <template v-if="!isSimplesOrMei">
        <div class="divider text-center" data-content="Reforma tributária (IBS/CBS)" />

        <div class="columns">
          <div class="column col-4 form-group">
            <label for="ibscbs-cst" class="form-label">CST</label>
            <input
              type="text"
              class="form-input"
              id="ibscbs-cst"
              v-model="form.service.ibsCbsCst"
              placeholder="200"
            >
          </div>
          <div class="column col-4 form-group">
            <label for="ibscbs-classtrib" class="form-label">Classificação Tributária</label>
            <input
              type="text"
              class="form-input"
              id="ibscbs-classtrib"
              v-model="form.service.ibsCbsClassTrib"
              placeholder="200029"
            >
          </div>
          <div class="column col-4 form-group">
            <label for="ibscbs-reduction" class="form-label">Redução</label>
            <div class="input-group">
              <dx-input-number
                class="form-input"
                id="ibscbs-reduction"
                v-model="form.service.ibsCbsReduction"
                maxlength="5"
                :precision="2"
              />
              <span class="input-group-addon">%</span>
            </div>
          </div>
        </div>
        </template>

        <div class="divider text-center" data-content="Numeração da nota" />

        <div class="columns">
          <div class="column col-3 form-group">
            <label for="fiscal-series" class="form-label">Série da NF</label>
            <input
              type="text"
              class="form-input"
              id="fiscal-series"
              v-model="form.series"
            >
          </div>
          <div class="column col-3 form-group">
            <label for="fiscal-sequential" class="form-label">Sequencial</label>
            <input
              type="text"
              class="form-input"
              id="fiscal-sequential"
              v-model="form.sequential"
            >
          </div>
        </div>
      </st-tab>

      <st-tab name="Dados de acesso" v-if="!loading && (isSaved || authMethod === 'credentials')">
        <template v-if="authMethod === 'certificate'">
        <div class="digital-certificate-info" v-if="hasCertificate">
          <div class="mb-1">
            <strong>Certificado digital</strong>
          </div>
          <div>
            <span class="mr-1">Nome:</span>
            <strong>{{ certificate.name }}</strong>
          </div>
          <div>
            <span class="mr-1">Valido até:</span>
            <strong>{{ certificate.expiresAt | date }}</strong>
          </div>
        </div>


        <div class="upload-area mb-2" :class="{ dragging: certificate.dragging }">
          <input
            type="file"
            accept=".pfx,.p12"
            class="input-file"
            @dragenter="certificate.dragging = true"
            @dragleave="certificate.dragging = false"
            @drop="certificate.dragging = false"
            @change="certificateUploadChange"
          >
          <div class="upload-inner">
            <fa-icon :icon="['fal', 'upload']"></fa-icon>
            <p>
              Arraste seu certificado digital aqui
              ou clique para selecionar<br>
              <small>(Extensão PFX ou P12)</small>
            </p>
          </div>
        </div>

        <div class="form-group">
          <label for="certificate-name" class="form-label">Certificado digital</label>
          <input
            class="form-input"
            :value="certificate.file ? certificate.file.name : '--'"
            :readonly="true"
          >
        </div>

        <div
          class="form-group"
          :class="{ 'has-error': $v.certificate.password.$error }"
        >
          <label for="certificate-password" class="form-label">Senha do certificado</label>
          <st-input-password
            id="certificate-password"
            v-model="certificate.password"
            placeholder="******"
          />
          <st-form-input-hint :validator="$v.certificate.password" />
        </div>

        <div class="text-center p-2">
          <button
            type="button"
            class="btn btn-primary"
            :class="{ loading: certificate.uploading }"
            @click="uploadCertificate"
            :disabled="!canUploadCertificate || certificate.uploading"
          >{{ hasCertificate ? 'Atualizar certificado' : 'Enviar certificado' }}</button>
        </div>
        </template>

        <template v-else>
          <div class="toast mb-2 mt-2">
            <fa-icon :icon="['fal', 'info-circle']" class="mr-1" />
            Este provedor autentica por <strong>usuário e senha</strong> do webservice
            da prefeitura. Informe os dados de acesso do prestador e clique em
            <strong>Salvar</strong>.
          </div>
          <div
            class="form-group"
            :class="{ 'has-error': $v.form.credentials.username.$error }"
          >
            <label for="ws-username" class="form-label">* Usuário</label>
            <input
              class="form-input"
              id="ws-username"
              type="text"
              v-model="form.credentials.username"
              placeholder="Usuário do webservice"
            >
            <st-form-input-hint :validator="$v.form.credentials.username" />
          </div>
          <div
            class="form-group"
            :class="{ 'has-error': $v.form.credentials.password.$error }"
          >
            <label
              v-if="form.credentials.hasPassword"
              class="form-switch"
            >
              <input
                type="checkbox"
                v-model="changingPassword"
                @change="onChangePasswordToggle"
              >
              <i class="form-icon"></i>Alterar senha
            </label>
            <st-input-password
              id="ws-password"
              v-model="form.credentials.password"
              :disabled="passwordDisabled"
              :placeholder="passwordPlaceholder"
            />
            <p v-if="passwordDisabled" class="form-input-hint">
              A senha já está cadastrada. Salve normalmente para mantê-la.
            </p>
            <st-form-input-hint v-else :validator="$v.form.credentials.password" />
          </div>
        </template>
      </st-tab>
    </st-tabs>

    <template v-slot:footer>
      <button
        type="button"
        class="btn btn-primary"
        :class="{ loading: saving }"
        :disabled="saving || loading"
        @click="save"
      >Salvar</button>
      <button
        type="button"
        class="btn ml-2"
        @click="close"
      >Sair</button>
    </template>
  </dx-modal>
</template>

<script>
import { mapState } from 'vuex';
import { required, email, requiredIf } from 'vuelidate/lib/validators';
import AblyService from '@/services/ably';
import { cnpj, cep, phone } from '@/data/validators';
import states from '@/data/states';
import { taxRegimes, SIMPLE_NATIONAL, MEI } from '@/data/fiscal-tax-regimes';
import { deepClone, mergeFrom } from '@/helpers/object';
import { clearNonNumbers, removeAccents } from '@/helpers/string';
import nbsItems from '@/data/nbs';
import nationalTaxationCodes from '@/data/national-taxation-codes';
import { parseFiscalSettingsXml, readXmlFile } from '@/helpers/nfse-xml';

const EVENT_UPDATE_NAME = 'fiscal-document-test-update';
const NBS_SEARCH_LIMIT = 50;
const NBS_SUGGESTIONS_VISIBLE = 3;
const NATIONAL_PROVIDER = 'national';
const NO_SPECIAL_TAX_REGIME = '0';
const PROFESSIONAL_SOCIETY_TAX_REGIME = '6';
const SIMPLES_FEDERAL_AND_MUNICIPAL = '1';
const SIMPLES_RETENTIONS = ['irRate', 'pisRate', 'cofinsRate', 'csllRate'];

const nbsIndex = nbsItems.map(item => ({
  item,
  digits: clearNonNumbers(item.code, ''),
  text: removeAccents(`${item.code} ${item.name}`.toLowerCase()),
}));

const nationalCodeIndex = nationalTaxationCodes.map(item => ({
  item,
  text: removeAccents(`${item.subitem} ${item.name} ${item.group}`.toLowerCase()),
}));

function formatRate(value) {
  return Number(value || 0).toFixed(2).replace('.', ',');
}

export default {
  data() {
    return {
      loading: false,
      loadingAddress: false,
      saving: false,
      fillingOut: false,
      testing: false,
      saved: false,
      postCodeValidated: false,
      changingPassword: false,
      form: this.blankForm(),
      certificate: {
        file: null,
        password: '',
        dragging: false,
        uploading: false,
        name: '',
        expiresAt: '',
      },
      xmlImport: {
        dragging: false,
        loading: false,
      },
      companyTaxRegime: {
        loading: false,
        requested: '',
        identity: '',
        status: '',
        label: '',
        allowedTaxRegimes: [],
        adjustedFrom: '',
      },
      specialTaxRegimes: [],
      ibsCbsDefaults: null,
      nbsSuggestionsExpanded: false,
      providers: [],
      states,
      taxRegimes,
    };
  },
  watch: {
    'form.identity': function watchIdentity() {
      this.loadCompanyTaxRegime();
    },
  },
  validations() {
    return {
      form: {
        companyName: { required },
        identity: { required, cnpj },
        email: { required, email },
        phone: { phone },
        municipalRegistration: { required },
        taxRegime: { required },
        specialTaxRegime: {},
        service: {
          nationalTaxationCode: { required },
          nbsCode: {
            required: requiredIf(() => this.isNbsRequired),
          },
          description: { required },
          issRate: { required },
        },
        address: {
          postalCode: { required, cep },
          street: { required },
          number: { required },
          city: { required },
          state: { required },
        },
        credentials: {
          username: {
            required: requiredIf(() => this.authMethod === 'credentials'),
          },
          password: {
            required: requiredIf(
              () => this.authMethod === 'credentials' && !this.passwordDisabled,
            ),
          },
        },
      },
      certificate: {
        password: { required },
      },
    };
  },
  mounted() {
    this.load();
    this.realtimeChannel.subscribe(EVENT_UPDATE_NAME, this.realtimeListener);
  },
  beforeDestroy() {
    this.realtimeChannel.unsubscribe(EVENT_UPDATE_NAME, this.realtimeListener);
  },
  computed: {
    ...mapState({
      user: state => state.auth.user,
    }),
    branch() {
      return this.user.branch;
    },
    passwordDisabled() {
      return this.form.credentials.hasPassword && !this.changingPassword;
    },
    passwordPlaceholder() {
      return this.passwordDisabled
        ? 'Senha já cadastrada'
        : 'Senha do webservice';
    },
    isSaved() {
      return this.saved;
    },
    isSimplesNacional() {
      return this.form.taxRegime === SIMPLE_NATIONAL;
    },
    selectedNbs() {
      const digits = clearNonNumbers(this.form.service.nbsCode, '');
      if (!digits) return null;

      const found = nbsIndex.find(entry => entry.digits === digits);
      return found ? found.item : { code: this.form.service.nbsCode, name: '' };
    },
    isNbsRequired() {
      return !!this.form.taxRegime && ![SIMPLE_NATIONAL, MEI].includes(this.form.taxRegime);
    },
    isSimplesOrMei() {
      return [SIMPLE_NATIONAL, MEI].includes(this.form.taxRegime);
    },
    isNationalProvider() {
      return this.form.provider === NATIONAL_PROVIDER;
    },
    selectedNationalCode() {
      const digits = clearNonNumbers(this.form.service.nationalTaxationCode, '');
      if (!digits) return null;

      const found = nationalTaxationCodes.find(item => item.code === digits);
      return found || { code: digits, name: '' };
    },
    nbsSuggestions() {
      const code = this.selectedNationalCode;
      if (!code || !code.nbs) return [];

      return code.nbs
        .map(nbs => nbsItems.find(item => item.code === nbs))
        .filter(Boolean);
    },
    visibleNbsSuggestions() {
      return this.nbsSuggestionsExpanded
        ? this.nbsSuggestions
        : this.nbsSuggestions.slice(0, NBS_SUGGESTIONS_VISIBLE);
    },
    hiddenNbsSuggestions() {
      return this.nbsSuggestions.length - this.visibleNbsSuggestions.length;
    },
    isNbsSuggested() {
      const digits = clearNonNumbers(this.form.service.nbsCode, '');
      return this.nbsSuggestions.some(item => clearNonNumbers(item.code, '') === digits);
    },
    regimeGuide() {
      const { taxRegime } = this.form;
      if (!taxRegime || this.loading) return null;

      const rows = this.isSimplesOrMei
        ? this.simplesGuideRows()
        : this.notOptantGuideRows();
      const pending = rows.filter(row => !row.ok);
      const regimeName = this.taxRegimes[taxRegime];

      if (pending.length) {
        return {
          rows,
          pending,
          title: `Padrão para ${regimeName}`,
          subtitle: 'É assim que a nota costuma ser aceita. '
            + 'Aplique e confira antes de salvar.',
        };
      }

      return {
        rows,
        pending,
        title: `Configuração no padrão para ${regimeName}`,
        subtitle: this.isSimplesOrMei
          ? 'IBS e CBS não vão na nota de optantes do Simples, por isso esses campos não aparecem.'
          : 'Regime especial, NBS e IBS/CBS conferem com o esperado.',
      };
    },
    availableTaxRegimes() {
      const { allowedTaxRegimes } = this.companyTaxRegime;
      if (!allowedTaxRegimes.length) return this.taxRegimes;

      return Object.keys(this.taxRegimes)
        .filter(value => allowedTaxRegimes.includes(value))
        .reduce((result, value) => ({ ...result, [value]: this.taxRegimes[value] }), {});
    },
    companyTaxRegimeFeedback() {
      const {
        loading, identity, status, label, adjustedFrom,
      } = this.companyTaxRegime;

      if (loading) {
        return {
          type: 'loading',
          icon: 'spinner-third',
          message: 'Consultando o CNPJ na Receita Federal',
        };
      }

      if (!identity) return null;

      if (status === 'unavailable') {
        return {
          type: 'unknown',
          icon: 'info-circle',
          message: 'A consulta à Receita Federal não respondeu agora. Confira o regime antes de salvar.',
        };
      }

      if (status === 'unknown') {
        return {
          type: 'unknown',
          icon: 'info-circle',
          message: 'A Receita Federal não informa se este CNPJ é optante do Simples. Confira o regime antes de salvar.',
        };
      }

      if (adjustedFrom) {
        return {
          type: 'adjusted',
          icon: 'exclamation-triangle',
          message: `CNPJ ${label} na Receita Federal. O regime ${this.taxRegimes[adjustedFrom]} não se aplica e foi ajustado. Clique em Salvar.`,
        };
      }

      return {
        type: 'confirmed',
        icon: 'check-circle',
        message: `CNPJ ${label} na Receita Federal`,
      };
    },
    authMethod() {
      const provider = this.providers.find(item => item.value === this.form.provider);
      return provider ? provider.authMethod : 'certificate';
    },
    hasCertificate() {
      return this.certificate.name && this.certificate.expiresAt;
    },
    canUploadCertificate() {
      return !this.$v.certificate.$error && this.certificate.file;
    },
    isPostalCodeValid() {
      return !this.$v.form.address.postalCode.$error && this.postCodeValidated;
    },
    isConfigured() {
      return this.isSaved && this.hasCertificate;
    },
    realtimeChannel() {
      const channelName = `branch.${this.user.branch.id}`;

      const { realtime } = AblyService.instance(this.user.id);
      return realtime.channels.get(channelName);
    },
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const { data } = await this.$http.get('/facility-settings/fiscal-document');
        if (data) {
          this.saved = !!data.identity;
          this.form = mergeFrom(this.blankForm(), data);
          if (!this.form.identity) {
            await this.fillForm();
          } else if (data.certificate) {
            this.certificate.name = data.certificate.filename;
            this.certificate.expiresAt = data.certificate.expiresAt;
          }
          await this.loadParams();
        }
      } catch (e) {
        this.$toast.error(e);
      } finally {
        this.loading = false;
      }
    },
    findNbs(text) {
      const term = removeAccents(text.trim().toLowerCase());
      const digits = clearNonNumbers(term, '');
      const isCodeSearch = /^[\d.\s]+$/.test(term);

      const matches = isCodeSearch
        ? entry => entry.digits.startsWith(digits)
        : entry => entry.text.includes(term);

      return nbsIndex
        .filter(matches)
        .slice(0, NBS_SEARCH_LIMIT)
        .map(entry => entry.item);
    },
    nbsLabel(item) {
      if (!item) return '';
      return item.name ? `${item.code} - ${item.name}` : item.code;
    },
    setNbs(item) {
      this.form.service.nbsCode = item ? clearNonNumbers(item.code, '') : '';
    },
    findNationalCode(text) {
      const term = removeAccents(text.trim().toLowerCase());
      const digits = clearNonNumbers(term, '');
      const isCodeSearch = /^[\d.\s]+$/.test(term);

      const matches = isCodeSearch
        ? entry => entry.item.code.startsWith(digits)
        : entry => entry.text.includes(term);

      return nationalCodeIndex
        .filter(matches)
        .slice(0, NBS_SEARCH_LIMIT)
        .map(entry => entry.item);
    },
    formatNationalCode(code) {
      return String(code).replace(/^(\d{2})(\d{2})(\d{2})$/, '$1.$2.$3');
    },
    nationalCodeLabel(item) {
      if (!item) return '';
      const code = this.formatNationalCode(item.code);
      return item.name ? `${code} - ${item.name}` : code;
    },
    setNationalCode(item) {
      this.form.service.nationalTaxationCode = item ? item.code : '';
      this.nbsSuggestionsExpanded = false;

      if (item && !this.form.service.nbsCode && this.nbsSuggestions.length === 1) {
        this.setNbs(this.nbsSuggestions[0]);
      }
    },
    specialTaxRegimeName(code) {
      const found = this.specialTaxRegimes.find(item => item.code === code);
      return found ? found.description : 'Não informado';
    },
    simplesGuideRows() {
      const { service } = this.form;
      const rows = [{
        key: 'specialTaxRegime',
        label: 'Regime especial',
        hint: 'Outro valor costuma ser recusado pela prefeitura',
        expected: this.specialTaxRegimeName(NO_SPECIAL_TAX_REGIME),
        current: this.specialTaxRegimeName(this.form.specialTaxRegime),
        ok: this.form.specialTaxRegime === NO_SPECIAL_TAX_REGIME,
        apply: () => {
          this.form.specialTaxRegime = NO_SPECIAL_TAX_REGIME;
        },
      }];

      if (this.form.taxRegime === SIMPLE_NATIONAL) {
        rows.push({
          key: 'taxCalculationRegimeSN',
          label: 'Apuração pelo Simples',
          expected: 'Tributos federais e municipal pelo SN',
          current: this.form.taxCalculationRegimeSN ? 'ISSQN por fora do SN' : 'Não informado',
          ok: this.form.taxCalculationRegimeSN === SIMPLES_FEDERAL_AND_MUNICIPAL,
          apply: () => {
            this.form.taxCalculationRegimeSN = SIMPLES_FEDERAL_AND_MUNICIPAL;
          },
        });
      }

      const retentions = SIMPLES_RETENTIONS.filter(key => Number(service[key]) > 0);
      rows.push({
        key: 'retentions',
        label: 'IR, PIS, COFINS e CSLL',
        hint: 'Já recolhidos no DAS',
        expected: '0,00%',
        current: retentions.map(key => key.replace('Rate', '').toUpperCase()).join(', '),
        ok: retentions.length === 0,
        apply: () => {
          SIMPLES_RETENTIONS.forEach((key) => {
            service[key] = 0;
          });
        },
      });

      return rows;
    },
    notOptantGuideRows() {
      const { service } = this.form;
      const rows = [{
        key: 'specialTaxRegime',
        label: 'Regime especial',
        hint: 'Sociedade de Profissionais só se a prefeitura enquadrou a empresa',
        expected: `${this.specialTaxRegimeName(NO_SPECIAL_TAX_REGIME)} ou `
          + `${this.specialTaxRegimeName(PROFESSIONAL_SOCIETY_TAX_REGIME)}`,
        current: this.specialTaxRegimeName(this.form.specialTaxRegime),
        ok: [NO_SPECIAL_TAX_REGIME, PROFESSIONAL_SOCIETY_TAX_REGIME]
          .includes(this.form.specialTaxRegime),
        apply: () => {
          this.form.specialTaxRegime = NO_SPECIAL_TAX_REGIME;
        },
      }, {
        key: 'nbsCode',
        label: 'Código NBS',
        hint: 'Obrigatório fora do Simples',
        expected: this.nbsSuggestions.length === 1 ? this.nbsSuggestions[0].code : 'Informado',
        current: 'Não informado',
        ok: !!service.nbsCode,
        apply: () => {
          if (this.nbsSuggestions.length === 1) this.setNbs(this.nbsSuggestions[0]);
        },
      }];

      const defaults = this.ibsCbsDefaults;
      if (defaults) {
        const ok = service.ibsCbsCst === defaults.cst
          && service.ibsCbsClassTrib === defaults.classTrib
          && Number(service.ibsCbsReduction) === Number(defaults.reduction)
          && Number(service.cbsRate) === Number(defaults.cbsRate)
          && Number(service.ibsRate) === Number(defaults.ibsRate);

        rows.push({
          key: 'ibsCbs',
          label: 'IBS e CBS',
          hint: 'Serviços de saúde (Anexo III)',
          expected: `CST ${defaults.cst}, classificação ${defaults.classTrib}, `
            + `CBS ${formatRate(defaults.cbsRate)}%, IBS ${formatRate(defaults.ibsRate)}%`,
          current: 'Diferente',
          ok,
          apply: () => {
            service.ibsCbsCst = defaults.cst;
            service.ibsCbsClassTrib = defaults.classTrib;
            service.ibsCbsReduction = defaults.reduction;
            service.cbsRate = defaults.cbsRate;
            service.ibsRate = defaults.ibsRate;
          },
        });
      }

      return rows;
    },
    applyRegimeGuide() {
      this.regimeGuide.pending.forEach(row => row.apply());
      this.$toast.success('Padrão aplicado. Confira e clique em Salvar.');
    },
    async loadCompanyTaxRegime() {
      const identity = clearNonNumbers(this.form.identity, '');

      if (identity.length !== 14) {
        this.resetCompanyTaxRegime();
        return;
      }

      if (identity === this.companyTaxRegime.requested) return;

      this.resetCompanyTaxRegime();
      this.companyTaxRegime.requested = identity;
      this.companyTaxRegime.loading = true;
      try {
        const { data } = await this.$http.get(
          '/facility-settings/fiscal-document/company-tax-regime',
          { params: { identity } },
        );
        if (this.companyTaxRegime.requested !== identity) return;

        this.companyTaxRegime.identity = data.identity;
        this.companyTaxRegime.status = data.status;
        this.companyTaxRegime.label = data.label;
        this.companyTaxRegime.allowedTaxRegimes = data.allowedTaxRegimes;
        this.applyCompanyTaxRegime();
      } catch (e) {
        if (this.companyTaxRegime.requested === identity) {
          this.companyTaxRegime.identity = identity;
          this.companyTaxRegime.status = 'unavailable';
        }
      } finally {
        if (this.companyTaxRegime.requested === identity) {
          this.companyTaxRegime.loading = false;
        }
      }
    },
    applyCompanyTaxRegime() {
      const { allowedTaxRegimes } = this.companyTaxRegime;
      this.companyTaxRegime.adjustedFrom = '';

      if (!allowedTaxRegimes.length || allowedTaxRegimes.includes(this.form.taxRegime)) return;

      if (this.form.taxRegime) {
        this.companyTaxRegime.adjustedFrom = this.form.taxRegime;
      }
      this.form.taxRegime = allowedTaxRegimes.length === 1 ? allowedTaxRegimes[0] : '';
    },
    resetCompanyTaxRegime() {
      this.companyTaxRegime.loading = false;
      this.companyTaxRegime.requested = '';
      this.companyTaxRegime.identity = '';
      this.companyTaxRegime.status = '';
      this.companyTaxRegime.label = '';
      this.companyTaxRegime.allowedTaxRegimes = [];
      this.companyTaxRegime.adjustedFrom = '';
    },
    async loadParams() {
      try {
        const params = {
          state: this.form.address.state,
          city: this.form.address.city,
        };
        const { data } = await this.$http.get('/facility-settings/fiscal-document/params', { params });
        this.specialTaxRegimes = data.specialTaxRegimes;
        this.ibsCbsDefaults = data.ibsCbsDefaults || null;
        this.providers = data.providers || [];
        if (!this.form.specialTaxRegime && this.specialTaxRegimes.length > 0) {
          this.form.specialTaxRegime = this.specialTaxRegimes[0].code;
        }
        this.applyIbsCbsDefaults(data.ibsCbsDefaults);
      } catch (e) {
        this.$toast.error(e);
      }
    },
    // Pré-preenche os campos do IBS/CBS (reforma) com os defaults do backend
    // apenas quando ainda não configurados — evita hardcodar valores no front.
    applyIbsCbsDefaults(defaults) {
      if (!defaults) return;
      const s = this.form.service;
      if (!s.ibsCbsCst) s.ibsCbsCst = defaults.cst || '';
      if (!s.ibsCbsClassTrib) s.ibsCbsClassTrib = defaults.classTrib || '';
      if (!s.ibsCbsReduction) s.ibsCbsReduction = defaults.reduction || 0;
      if (!s.cbsRate) s.cbsRate = defaults.cbsRate || 0;
      if (!s.ibsRate) s.ibsRate = defaults.ibsRate || 0;
    },
    onChangePasswordToggle() {
      this.form.credentials.password = '';
      this.$v.form.credentials.password.$reset();
    },
    validateForm() {
      this.$v.form.$touch();
      return !this.$v.form.$error;
    },
    validatePostalCode() {
      this.$v.form.address.postalCode.$touch();
      return !this.$v.form.address.postalCode.$error;
    },
    async save() {
      if (!this.validateForm()) {
        this.$toast.error('Existem erros de preenchimento');
        return;
      }

      this.saving = true;
      try {
        const postData = deepClone(this.form);
        postData.identity = clearNonNumbers(postData.identity);
        postData.address.postalCode = clearNonNumbers(postData.address.postalCode);

        // Credenciais de webservice: envia só para providers de login/senha; a
        // senha vazia mantém a atual (backend preserva). Nunca envia hasPassword.
        if (this.authMethod === 'credentials') {
          postData.credentials = { username: this.form.credentials.username };
          if (this.form.credentials.password) {
            postData.credentials.password = this.form.credentials.password;
          }
        } else {
          delete postData.credentials;
        }

        await this.$http.post('/facility-settings/fiscal-document', postData);
        this.saved = true;
        this.companyTaxRegime.adjustedFrom = '';
        this.$toast.success('Dados salvos com sucesso');
      } catch (e) {
        this.$toast.error(e);
      }
      this.saving = false;
    },
    async xmlUploadChange(e) {
      e.preventDefault();
      const [file] = e.target.files;
      // Limpa o input para permitir reenviar o mesmo arquivo.
      e.target.value = '';
      if (!file) return;

      this.xmlImport.loading = true;
      try {
        const { data, warnings } = parseFiscalSettingsXml(await readXmlFile(file));
        await this.applyXmlData(data);
        this.showXmlImportResult(warnings);
      } catch (error) {
        this.$toast.error(error.message || error);
      }
      this.xmlImport.loading = false;
    },
    /**
     * Preenche os campos que vieram no XML. Nada é salvo automaticamente: o
     * usuário confere e clica em Salvar (ou fecha o modal para descartar).
     */
    async applyXmlData(data) {
      const assign = (target, source) => {
        Object.keys(source).forEach((key) => {
          if (source[key] !== null && source[key] !== undefined) {
            target[key] = source[key];
          }
        });
      };

      const { address, service, ...company } = data;
      assign(this.form, company);
      assign(this.form.service, service);

      if (address.postalCode) {
        this.form.address.postalCode = address.postalCode;
        // O XML traz o código IBGE do município, que não resolvemos no front —
        // a cidade/estado saem do CEP, pelo mesmo caminho do preenchimento manual.
        await this.loadAddressByPostalCode();
      }
      assign(this.form.address, address);
      this.applyCompanyTaxRegime();
    },
    showXmlImportResult(warnings) {
      if (!warnings.length) {
        this.$toast.success('Dados importados do XML. Confira e clique em Salvar.');
        return;
      }

      const items = warnings.map(item => `<li>${item}</li>`).join('');
      this.$dialog.show('Dados importados do XML', {
        html: `<p>Os campos que não vieram no XML continuam como estavam.
          Confira os pontos abaixo antes de salvar:</p><ul>${items}</ul>`,
      });
    },
    certificateUploadChange(e) {
      e.preventDefault();
      if (e.target.files[0]) {
        ([this.certificate.file] = e.target.files);
      } else {
        this.certificate.file = null;
      }
    },
    resetCertificateForm() {
      this.certificate.file = null;
      this.certificate.password = '';
      this.$v.certificate.$reset();
    },
    async uploadCertificate() {
      this.$v.certificate.$touch();
      if (this.$v.certificate.$error) {
        return;
      }

      this.certificate.uploading = true;
      try {
        const formData = new FormData();
        formData.append('file', this.certificate.file);
        formData.append('password', this.certificate.password);

        const { data: certificateData } = await this.$http.post(
          '/facility-settings/fiscal-document/certificate',
          formData,
        );
        this.certificate.name = certificateData.filename;
        this.certificate.expiresAt = certificateData.expiresAt;
        this.resetCertificateForm();

        this.$toast.success('Certificado enviado com sucesso');
      } catch (e) {
        this.$toast.error(e);
      }
      this.certificate.uploading = false;
    },
    async test() {
      this.testing = true;
      try {
        await this.$http.post('/facility-settings/fiscal-document/test');
      } catch (e) {
        this.testing = false;
        this.$toast.error(e.response?.data?.message || e);
      }
    },
    realtimeListener({ data }) {
      const isSuccess = data.status === 'issued';
      const message = isSuccess
        ? 'Teste de emissão concluído com sucesso'
        : data.statusReason || 'Erro desconhecido';

      this.$dialog.show(isSuccess ? 'Sucesso' : 'Falha', {
        html: `<div class="text-pre${isSuccess ? '' : ' text-error'}">${message}</div>`,
      });

      if (data.sequential) {
        this.form.sequential = data.sequential;
      }
      this.testing = false;
    },
    close() {
      this.$emit('close');
    },
    async fillForm() {
      this.fillingOut = true;
      try {
        const { data } = await this.$http.get(`/branches/${this.branch.id}?user=false`);
        this.form.companyName = data.companyName;
        this.form.identity = data.identity.value;
        this.form.name = data.name;
        this.form.email = data.email;
        this.form.phone = data.phone || data.cellphone;

        const [address] = data.addresses;
        if (address) {
          const [street, number] = address.addressLine1.split(',');
          this.form.address.postalCode = address.postalCode;
          this.form.address.street = street ? street.trim() : '';
          this.form.address.number = number ? number.trim() : '';
          this.form.address.complement = address.addressLine2;
          this.form.address.neighborhood = address.neighborhood;
          this.form.address.city = address.city;
          this.form.address.state = address.state;

          this.postCodeValidated = !!address.city && !!address.state;
        }
      } catch (e) {
        this.$toast.error(e);
      }
      this.fillingOut = false;
    },
    async loadAddressByPostalCode() {
      const postalCode = clearNonNumbers(this.form.address.postalCode);

      if (this.validatePostalCode() && postalCode.length === 8 && !this.loadingAddress) {
        this.loadingAddress = true;
        this.postCodeValidated = false;

        try {
          const data = await this.$cep.get(postalCode);
          this.form.address.city = data.city;
          this.form.address.state = data.state;
          this.form.address.neighborhood = data.neighborhood;
          this.form.address.street = data.street;

          this.postCodeValidated = !!data.city && !!data.state;
          if (!this.postCodeValidated) {
            this.$toast.error('CEP não encontrado');
          }
        } catch (e) {
          this.$toast.error(e);
        }

        this.loadingAddress = false;
      }
    },
    blankForm() {
      return {
        companyName: '', // Razão Social
        name: '', // Nome Fantasia
        identity: '',
        email: '',
        phone: '',
        stateRegistration: '',
        municipalRegistration: '',
        taxRegime: '',
        specialTaxRegime: '',
        taxCalculationRegimeSN: '',
        culturalIncentive: false,
        service: {
          nationalTaxationCode: '',
          municipalCode: '',
          nbsCode: '',
          cnae: '',
          issRate: 0,
          irRate: 0,
          pisRate: 0,
          cofinsRate: 0,
          csllRate: 0,
          inssRate: 0,
          cbsRate: 0,
          ibsRate: 0,
          ibsCbsCst: '',
          ibsCbsClassTrib: '',
          ibsCbsReduction: 0,
          description: '',
        },
        series: '1',
        sequential: 1,
        provider: 'national',
        credentials: {
          username: '',
          password: '',
          hasPassword: false,
        },
        address: {
          postalCode: '',
          street: '',
          number: '',
          complement: '',
          neighborhood: '',
          city: '',
          state: '',
        },
      };
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/_variables";

#integration-fiscal-document-modal {
  .modal .modal-container {
    max-width: 40rem;
  }

  .nbs-option {
    display: flex;
    gap: $unit-2;
    .option-group {
      color: $gray-color-dark;
      display: block;
      font-size: $font-size-xs;
    }
  }

  .nbs-option-code {
    color: $primary-color;
    flex-shrink: 0;
    font-variant-numeric: tabular-nums;
    font-weight: 600;
  }

  .field-warning {
    color: darken(#F2C94C, 30%);
  }

  .nbs-suggestions {
    background-color: $gray-color-ultra-light;
    border: $border-width solid $border-color;
    border-left-width: $unit-1;
    border-radius: $border-radius;
    margin-top: $unit-2;
    padding: $unit-2;
    &.nbs-suggestions-mismatch {
      background-color: #FFF8E6;
      border-color: #F2C94C;
      border-left-color: $warning-color;
      .nbs-suggestions-header svg {
        color: darken(#F2C94C, 20%);
      }
      .nbs-suggestions-list {
        border-color: #F5DD8E;
      }
    }
  }

  .nbs-suggestions-header {
    align-items: center;
    color: $gray-color-900;
    display: flex;
    font-size: $font-size-sm;
    gap: $unit-2;
    line-height: 1.3;
    padding: 0 $unit-1 $unit-2;
    svg {
      color: $primary-color;
      flex-shrink: 0;
    }
  }

  .nbs-suggestions-list {
    background-color: $light-color;
    border: $border-width solid $border-color;
    border-radius: $border-radius;
    overflow: hidden;
  }

  .nbs-suggestion {
    align-items: center;
    background-color: transparent;
    border: 0;
    color: $gray-color-900;
    cursor: pointer;
    display: flex;
    font-size: $font-size-sm;
    gap: $unit-2;
    line-height: 1.3;
    padding: $unit-2 $unit-3;
    text-align: left;
    transition: background-color .15s ease;
    width: 100%;
    & + .nbs-suggestion {
      border-top: $border-width solid $border-color;
    }
    .nbs-option-code {
      width: 5.2rem;
    }
    &:hover {
      background-color: $gray-color-ultra-light;
      .nbs-suggestion-name {
        overflow: visible;
        white-space: normal;
      }
      .nbs-suggestion-action {
        color: $primary-color;
      }
    }
    &:focus {
      box-shadow: none;
      outline: none;
    }
  }

  .nbs-suggestion-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .nbs-suggestion-action {
    color: $gray-color-dark;
    flex-shrink: 0;
    font-size: $font-size-xs;
    font-weight: 600;
  }

  .nbs-suggestion-more {
    color: $primary-color;
    font-size: $font-size-xs;
    font-weight: 600;
    justify-content: center;
  }

  .regime-guide {
    border: $border-width solid $border-color;
    border-left-width: $unit-1;
    border-radius: $border-radius;
    padding: $unit-2 $unit-3;
    &.regime-guide-pending {
      background-color: #FFF8E6;
      border-color: #F2C94C;
      border-left-color: $warning-color;
      .regime-guide-header > svg {
        color: darken(#F2C94C, 20%);
      }
    }
    &.regime-guide-ok {
      background-color: #F2FAF5;
      border-color: #BFE3CD;
      border-left-color: #2FA36B;
      .regime-guide-header > svg,
      .regime-guide-title strong {
        color: #1F7A4E;
      }
    }
  }

  .regime-guide-header {
    align-items: center;
    display: flex;
    gap: $unit-2;
    > svg {
      flex-shrink: 0;
      font-size: .9rem;
    }
    .btn {
      flex-shrink: 0;
      margin-left: auto;
    }
  }

  .regime-guide-title {
    line-height: 1.3;
    strong {
      color: $gray-color-900;
      display: block;
      font-weight: 600;
    }
    small {
      color: $gray-color-dark;
      font-size: $font-size-xs;
    }
  }

  .regime-guide-rows {
    background-color: $light-color;
    border: $border-width solid #F5DD8E;
    border-radius: $border-radius;
    margin-top: $unit-2;
  }

  .regime-guide-row {
    align-items: center;
    display: grid;
    gap: $unit-2;
    grid-template-columns: 11rem 1fr auto;
    padding: $unit-2 $unit-3;
    & + .regime-guide-row {
      border-top: $border-width solid $border-color;
    }
  }

  .regime-guide-label {
    line-height: 1.3;
    span {
      color: $gray-color-900;
      display: block;
      font-weight: 600;
    }
    small {
      color: $gray-color-dark;
      font-size: $font-size-xs;
    }
  }

  .regime-guide-expected {
    color: $gray-color-900;
    font-size: $font-size-sm;
  }

  .regime-guide-pill {
    align-items: center;
    background-color: $light-color;
    border: $border-width solid;
    border-radius: 1rem;
    display: inline-flex;
    font-size: $font-size-xs;
    gap: $unit-1;
    padding: 0 $unit-2;
    white-space: nowrap;
    &.ok {
      border-color: #BFE3CD;
      color: #1F7A4E;
    }
    &.diff {
      border-color: #F2C94C;
      color: darken(#F2C94C, 35%);
    }
  }

  .company-tax-regime {
    align-items: center;
    background-color: $gray-color-ultra-light;
    border: $border-width solid $border-color;
    border-left-width: $unit-1;
    border-radius: $border-radius;
    color: $gray-color-dark;
    display: flex;
    font-size: $font-size-sm;
    line-height: 1.3;
    padding: $unit-2 $unit-3;
    svg {
      flex-shrink: 0;
      font-size: .8rem;
      margin-right: $unit-2;
    }
    &.confirmed {
      background-color: #F2FAF5;
      border-color: #BFE3CD;
      border-left-color: #2FA36B;
      color: #1F7A4E;
    }
    &.unknown,
    &.adjusted {
      background-color: #FFF8E6;
      border-color: #F2C94C;
      border-left-color: $warning-color;
      color: $gray-color-900;
      svg {
        color: darken(#F2C94C, 20%);
      }
    }
  }

  .digital-certificate-info {
    background-color: rgba($info-color, .1);
    border: $border-width solid $info-color;
    border-radius: $border-radius;
    margin: $layout-spacing 0;
    padding: $layout-spacing;
    strong {
      font-weight: 600;
    }
  }

  .upload-area {
    align-items: center;
    background-color: lighten($gray-color-light, 5%);
    border: lighten($gray-color, 5%) $unit-h dashed;
    display: flex;
    height: 7rem;
    justify-content: center;
    margin-top: $layout-spacing;
    position: relative;
    &.dragging {
      border-color: $primary-color;
    }
    .input-file {
      display: block;
      height: 100%;
      left: 0;
      opacity: 0;
      position: absolute;
      top: 0;
      width: 100%;
    }
    .upload-inner {
      display: flex;
      align-items: center;
    }
    svg {
      font-size: 1.8rem;
    }
    p {
      margin: 0 0 0 $layout-spacing;
      text-align: center;
    }
    &.xml-import {
      height: auto;
      padding: $layout-spacing;
      small {
        color: $gray-color-dark;
      }
    }
  }
}
</style>
